import {
  failure,
  Failure,
  isFailure,
  isOk,
  mapToSigners,
  ok,
  Ok,
  signer,
  Signer,
} from "../common/common";
import { AxiosInstance, AxiosResponse } from "axios";
import AlertMessageStore from "../stores/AlertMessageStore";
import { makeAutoObservable, toJS } from "mobx";
import { History } from "history";
import { logDebug, logError } from "../utils/log";
import { compact, identity, remove } from "lodash";
import { fromQueryString, toQueryString } from "../utils/queryParams";
import {
  ResendDocumentForSigningData,
  ViewDocumentSigningStatusDialogModel,
} from "../dbot/summary/summaryDomain";
import {
  DocumentApiClient,
  DocumentSigningStatus,
  GetDocumentSigningStatusResponse,
} from "../document/documentApi";
import {
  emptyFileUploadConfig,
  FileUploadConfig,
  FileUploadValidationError,
  isFileUploadValidationError,
  validateFiles,
} from "../fileUpload/fileUploadDomain";
import { downloadPdf } from "../api/DownloadService";
import {
  ArchiveDocumentStatus,
  ArchiveDocumentType,
  ArchiveEntryAction,
  ArchivePathNode,
  ArchiveRootFolderType,
  getActionIcon,
  getActionLabel,
} from "./commonArchiveDomain";
import {
  adminArchive,
  navigateToProject,
  navigateToProjectOverview,
  previewBoardMeetingForAdmin,
} from "../utils/navigation";
import { DropdownMenuItems, DropdownMenuItemType } from "../components-v2/Dropdown";
import { IconName } from "../components-v2/iconMapping";
import { downloadFile, getFilenameFromHeaders } from "../api";
import { isExtensionPdf } from "../utils/files";
import { CompanyInfo, CompanyRole } from "../types/CompanyInfo";
import { ExtendedShareholderDTO } from "../types/Shareholder";
import { MeetingType } from "../meetings/meetingsDomain";
import { IconProps } from "../components-v2/Icon";
import { when } from "../utils/functions";
import { TFunction, TMessage } from "../i18n/tt";
import { User } from "../types/UserInfo";
import { AIClient } from "../ai/AIAssistant";
import { getFullName, sortedBoardAndCEO } from "../utils/Roles";
import {
  BooleanFieldModel,
  mapSendSealedCopyTo,
  RadioFieldModel,
  SignerModel,
  SignersForm,
  SigningSettings,
  StringFieldModel,
} from "../common/commonForms";
import { Language } from "../i18n/languages";
import { DialogStore } from "../dialogs/dialogDomain";
import { ObjectPermission } from "../types/Permissions";
import { toDocumentReference } from "../utils/UrnReference";
import { FilePreviewProps } from "../components-v2/FilePreview";
import { CustomRoleForUser } from "../common/customRole";
import { isAlertDismissed, UserPrefs } from "../stores/userPrefsDomain";
import { LocalizedTextEditorModel } from "../text/localizedTextEditorDomain";

function isRootFolderType(s?: string): s is ArchiveRootFolderType {
  return s !== undefined && (Object.values(ArchiveRootFolderType) as string[]).includes(s);
}

export type ArchiveEntry = {
  id: string;
  parentId?: string;
  type: ArchiveDocumentType;
  name: string;
  updated?: string;
  signingStatus?: DocumentSigningStatus;
  isFolder?: boolean;
  isReadonly?: boolean;
  projectId?: string;
  meetingType?: MeetingType;
  meetingDate?: string;
  linkTargetType?: ArchiveDocumentType;
  linkTargetId?: string;
  folderDescription?: string;
  folderIcon?: IconName;
  folderIconColor?: string;
};

export const isSigningInProgress = (entry: ArchiveEntry): boolean =>
  entry.signingStatus === DocumentSigningStatus.SENT_FOR_SIGNING ||
  entry.signingStatus === DocumentSigningStatus.SIGNING_IN_PROGRESS;

export type ArchiveRootFolder = {
  folderType: ArchiveRootFolderType;
  folderId: string;
  folderTitle?: string;
  folderDescription?: string;
  folderIcon?: IconName;
  folderIconColor?: string;
  numberOfPermissions?: number;
};

export type ListArchiveFilter = {
  signingStatuses?: DocumentSigningStatus[];
  parentId?: string;
  status?: ArchiveDocumentStatus;
  types?: ArchiveDocumentType[];
  limit?: number;
  beforeEntry?: string;
  orderBy?: { field: "UPDATED" | "NAME"; descending?: boolean }[];
};

export type ListArchiveResponse = {
  folderId?: string;
  entries: ArchiveEntry[];
  path?: ArchivePathNode[];
  fileUploadConfig: FileUploadConfig;
  limit?: number;
  beforeEntry?: string;
  numberOfPermissions: number;
};

export type RestoreResponse = {
  entry: ArchiveEntry;
  path: ArchivePathNode[];
};

export type DestinationFolder = {
  id: string;
  type: ArchiveDocumentType;
  name: string;
  rootFolder?: ArchiveRootFolderType;
  rootFolderTitle?: string;
};

export type DestinationFoldersResponse = {
  entries: DestinationFolder[];
};

export type ArchiveEvent =
  | { type: "open-create-folder-dialog"; existingEntries: string[]; parentId?: string }
  | {
      type: "open-rename-dialog";
      existingEntries: string[];
      isRootFolder: boolean;
      entryId: string;
      name: string;
      description?: string;
    }
  | { type: "open-delete-archive-forever"; entryId: string; name: string }
  | { type: "open-change-permission-dialog"; title: string; folderId: string }
  | { type: "update-permission-result"; numberOfPermissions: number }
  | {
      type: "delete-signature-request";
      entryId: string;
      projectId?: string;
      onConfirmCancel?: ArchiveEvent;
    }
  | { type: "create-folder" }
  | { type: "create-folder-result"; result: Ok<ArchiveEntry> }
  | { type: "delete"; entryId: string; permanent?: boolean }
  | { type: "delete-archive"; entryId: string; permanent?: boolean }
  | { type: "delete-result"; entryId: string; result: Ok | Failure<TMessage> }
  | { type: "download"; entryId: string }
  | { type: "download-result"; entryId: string; result: Ok | Failure<TMessage> }
  | { type: "download-signed-document"; entryId: string }
  | { type: "download-signed-document-result"; entryId: string; result: Ok | Failure<TMessage> }
  | { type: "get-destination-folders"; entry: ArchiveEntry }
  | { type: "open-empty-trash-dialog" }
  | { type: "empty-trash" }
  | { type: "empty-trash-result"; result: Ok | Failure<TMessage> }
  | {
      type: "get-destination-folders-result";
      entry: ArchiveEntry;
      result: Ok<DestinationFoldersResponse> | Failure;
    }
  | { type: "get-signing-status"; entryId: string }
  | {
      type: "get-signing-status-result";
      entryId: string;
      result: Ok<GetDocumentSigningStatusResponse> | Failure<TMessage>;
    }
  | { type: "load"; rootFolder: ArchiveRootFolderType; folderId?: string; roleId?: string }
  | { type: "load-folder-counts" }
  | {
      type: "load-result";
      rootFolder: ArchiveRootFolderType;
      folderId?: string;
      result: Ok<ListArchiveResponse> | Failure<TMessage>;
    }
  | { type: "load-root-folders"; onSuccess?: ArchiveEvent }
  | { type: "init"; rootFolder?: string; queryString: string }
  | { type: "move"; state: MoveState }
  | { type: "move-result"; state: MoveState; result: Ok | Failure }
  | { type: "move"; state: MoveState }
  | {
      type: "navigate";
      rootFolder: ArchiveRootFolderType;
      folderId?: string;
      replace?: boolean;
      roleId?: string;
    }
  | { type: "navigate-to-board-meeting"; projectId: string; view: "notice" | "minutes" }
  | { type: "navigate-to-dbot"; projectId: string; documentId?: string }
  | { type: "navigate-to-new-project" }
  | { type: "navigate-to-payment" }
  | { type: "rename" }
  | { type: "rename-result"; isRootFolder: boolean; result: Ok<ArchiveEntry> }
  | ResendSignedDocument
  | {
      type: "resend-for-signing-result";
      entryId: string;
      signedDocumentId: string;
      result: Ok<GetDocumentSigningStatusResponse> | Failure<TMessage>;
    }
  | { type: "restore"; entryId: string }
  | { type: "restore-result"; entryId: string; result: Ok<RestoreResponse> | Failure<TMessage> }
  | { type: "send-for-signing-success" }
  | {
      type: "send-for-signing";
      entryId: string;
      signers: Signer[];
      emailBody: string;
      signingSettings: SigningSettings;
    }
  | { type: "send-for-signing-result"; entryId: string; result: Ok | Failure<TMessage> }
  | { type: "upload"; files: File[]; folderId: string }
  | { type: "upload-result"; result: Ok<ArchiveEntry[]> | Failure<TMessage> }
  | {
      type: "validate-files-for-upload";
      folderId: string;
      fileUploadConfig: FileUploadConfig;
      files: File[];
    }
  | {
      type: "validate-files-for-upload-success";
      folderId: string;
      fileUploadConfig: FileUploadConfig;
      files: File[];
    }
  | {
      type: "validate-files-for-upload-failure";
      folderId?: string;
      fileUploadConfig: FileUploadConfig;
      files: File[];
      error: unknown;
    }
  | { type: "requires-subscription" };

export type ResendSignedDocument = {
  type: "resend-for-signing";
  entryId: string;
  signedDocumentId: string;
  data: ResendDocumentForSigningData;
};

export type ArchiveDispatch = (event: ArchiveEvent) => void;

export const archiveEntryUrl = (
  { organisationNumber }: { organisationNumber: string },
  entry: ArchiveEntry,
) => {
  if (entry.linkTargetType === ArchiveDocumentType.ATTACHMENT && entry.projectId) {
    return `/project/${entry.projectId}/attachment/${entry.linkTargetId}`;
  }
  return `/archive/${organisationNumber}/download/${entry.id}`;
};

class ArchiveApiClient {
  readonly http: AxiosInstance;
  readonly organisationNumber: string;

  constructor(http: AxiosInstance, organisationNumber: string) {
    this.http = http;
    this.organisationNumber = organisationNumber;
  }

  loadRootFolders(): Promise<Array<ArchiveRootFolder>> {
    return this.http
      .get(`/archive/${this.organisationNumber}/root-folders`)
      .then((res) => res.data);
  }

  list(filter: ListArchiveFilter): Promise<ListArchiveResponse> {
    return this.http
      .post(`/archive/${this.organisationNumber}/list`, filter)
      .then((res) => res.data);
  }

  getDestinationFolders(entryId: string): Promise<ListArchiveResponse> {
    return this.http
      .get(`/archive/${this.organisationNumber}/destination-folders/${entryId}`)
      .then((res) => res.data);
  }

  upload({ files, folderId }: { files: File[]; folderId: string }): Promise<ArchiveEntry[]> {
    const formData = new FormData();
    for (const file of files) {
      formData.append("files", file);
    }
    return this.http
      .post(`/archive/${this.organisationNumber}/upload/${folderId}`, formData)
      .then((res) => res.data);
  }

  delete({ entryId, permanent }: { entryId: string; permanent?: boolean }): Promise<void> {
    return this.http.delete(
      `/archive/${this.organisationNumber}/${entryId}${toQueryString({ permanent })}`,
    );
  }

  emptyTrash() {
    return this.http.post(`/archive/${this.organisationNumber}/empty-trash`);
  }

  download(entry: ArchiveEntry): Promise<AxiosResponse<Blob>> {
    return this.http.get(archiveEntryUrl(this, entry), {
      responseType: "blob",
    });
  }

  createFolder({
    name,
    parentId,
    description,
  }: {
    name: string;
    parentId: string | undefined;
    description?: string;
  }): Promise<ArchiveEntry> {
    return this.http
      .post(`/archive/create-folder`, {
        organisationNumber: this.organisationNumber,
        name,
        parentId,
        description,
      })
      .then((res) => res.data);
  }

  rename({
    entryId,
    name,
    description,
  }: {
    entryId: string;
    name: string;
    description?: string;
  }): Promise<ArchiveEntry> {
    return this.http
      .post<ArchiveEntry>(`/archive/rename`, {
        organisationNumber: this.organisationNumber,
        id: entryId,
        name,
        description,
      })
      .then((res) => res.data);
  }

  restore({ entryId }: { entryId: string }): Promise<RestoreResponse> {
    return this.http
      .post<RestoreResponse>(`/archive/restore`, {
        id: entryId,
      })
      .then((res) => res.data);
  }

  move(body: { destinationId: string | undefined; entryId: string }) {
    return this.http.post<void>(`/archive/${this.organisationNumber}/move`, body);
  }

  cancelSignatureRequest(id: string): Promise<void> {
    return this.http
      .post(`/sign/cancel-signature-request`, {
        id,
      })
      .then((res) => res.data);
  }

  reopenLockedDocument(id: string): Promise<void> {
    return this.http.post(`/document/reopen-locked-document/${id}`, {}).then((res) => res.data);
  }
}

export type ArchiveDialogState =
  | { type: "none" }
  | CreateFolderState
  | RenameState
  | ConfirmDeleteArchiveForeverState
  | ConfirmEmptyTrashState;

export type RootFolderState =
  | { type: "ready" }
  | { type: "loading" }
  | { type: "uploading" }
  | { type: "drop-uploading" }
  | { type: "requires-subscription" }
  | ConfirmCancelSignatureRequestState
  | ConfirmDeleteForeverState
  | MoveState
  | PreviewState
  | SendForSigningState
  | SigningStatusState;

export type RootFolderError = FileUploadValidationError | { type: "message"; message: TMessage };

export type CreateFolderState = {
  type: "create-folder";
  parentId: string | undefined;
  name: StringFieldModel;
  showDescription: BooleanFieldModel;
  description: LocalizedTextEditorModel;
  submitting?: boolean;
};

export type PreviewState = {
  type: "preview";
  entry: ArchiveEntry;
  file: { url: string };
};

export type RenameState = {
  type: "rename";
  isRootFolder: boolean;
  entryId: string;
  name: StringFieldModel;
  description: LocalizedTextEditorModel;
  submitting?: boolean;
};

export type MoveState = {
  type: "move";
  entry: ArchiveEntry;
  destinationFolders: DestinationFolder[];
  selectedDestinationFolder?: DestinationFolder;
  confirmChange: boolean;
  isSubmitting: boolean;
  canSubmit: boolean;
};

export type SendForSigningState = {
  type: "send-for-signing";
  entry: ArchiveEntry;
  form: SignersForm;
  title: TMessage;
  isSending?: boolean;
};

export type SigningStatusState = {
  type: "signing-status";
  dialogModel: ViewDocumentSigningStatusDialogModel;
};

export type ConfirmDeleteForeverState = {
  type: "confirm-delete-forever";
  entry: ArchiveEntry;
  loading?: boolean;
};

export type ConfirmCancelSignatureRequestState = {
  type: "confirm-delete-signature-request";
  entry: ArchiveEntry;
  onConfirmCancel?: ArchiveEvent;
  loading?: boolean;
};

export type ConfirmDeleteArchiveForeverState = {
  type: "confirm-delete-archive-forever";
  folderId: string;
  name: string;
  confirmChange: boolean;
  loading: boolean;
  canSubmit: boolean;
};

export type ConfirmEmptyTrashState = {
  type: "confirm-empty-trash";
  loading: boolean;
};

export function toListArchiveFilter({
  rootFolder,
  rootFolderId,
  folderId,
}: {
  rootFolder: ArchiveRootFolderType;
  rootFolderId?: string;
  folderId?: string;
}): ListArchiveFilter {
  switch (rootFolder) {
    case "deleted":
      return {
        status: ArchiveDocumentStatus.DELETED,
        orderBy: [{ field: "UPDATED", descending: true }],
      };
    default:
      return { parentId: folderId ?? rootFolderId };
  }
}

export const load = ({
  rootFolder,
  folderId,
  roleId,
}: {
  rootFolder: ArchiveRootFolderType;
  folderId?: string;
  roleId?: string;
}): ArchiveEvent => ({
  type: "load",
  rootFolder,
  folderId,
  roleId,
});

export const toEntryMenuItem = ({
  action,
  t,
  onSelect,
}: {
  action: ArchiveEntryAction;
  t: TFunction;
  onSelect: () => void;
}): DropdownMenuItemType => ({
  type: "item",
  icon: getActionIcon(action),
  label: t(getActionLabel(action)),
  color: getActionColor(action),
  onSelect,
});

export const createMoveState = (
  entry: ArchiveEntry,
  destinationFolders: DestinationFolder[],
): MoveState => ({
  type: "move",
  entry,
  destinationFolders,
  confirmChange: false,
  isSubmitting: false,
  get canSubmit() {
    return Boolean(this.selectedDestinationFolder) && !this.isSubmitting && this.confirmChange;
  },
});

export const createDeleteArchiveState = (
  folderId: string,
  name: string,
): ConfirmDeleteArchiveForeverState => ({
  type: "confirm-delete-archive-forever",
  folderId,
  name,
  confirmChange: false,
  loading: false,
  get canSubmit() {
    return !this.loading && this.confirmChange;
  },
});

export const createEmptyTrashState = (): ConfirmEmptyTrashState => ({
  type: "confirm-empty-trash",
  loading: false,
});

export class RootFolderModel {
  readonly dispatch: ArchiveDispatch;
  readonly rootFolder: ArchiveRootFolderType;
  readonly queryString: string;
  readonly messageStore: AlertMessageStore;
  readonly organisationNumber: string;
  data?: ListArchiveResponse;
  state: RootFolderState = { type: "ready" };
  currentActionByEntry: Record<ArchiveEntry["id"], ArchiveEntryAction | undefined> = {};
  error?: RootFolderError;
  boardMembersAndCeo?: CompanyRole[];
  shareholders?: ExtendedShareholderDTO[];
  companyInfo?: CompanyInfo;
  userRole: CustomRoleForUser;
  user: User | null;
  aiClient: AIClient;
  textFilter = new StringFieldModel();
  fileTypeFilter: RadioFieldModel<ArchiveFileTypeFilter>;
  signaturesFilter: RadioFieldModel<ArchiveSignaturesFilter>;
  numberOfPermissions = 0;
  userPrefs: UserPrefs;
  roleId?: string;

  constructor(
    messageStore: AlertMessageStore,
    dispatch: ArchiveDispatch,
    rootFolder: ArchiveRootFolderType,
    queryString: string,
    organisationNumber: string,
    aiClient: AIClient,
    userRole: CustomRoleForUser,
    userPrefs: UserPrefs,
    boardMembersAndCeo?: CompanyRole[],
    shareholders?: ExtendedShareholderDTO[],
    companyInfo?: CompanyInfo,
    user: User | null = null,
    roleId?: string,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.dispatch = dispatch;
    this.messageStore = messageStore;
    this.rootFolder = rootFolder;
    this.queryString = queryString;
    this.organisationNumber = organisationNumber;
    this.boardMembersAndCeo = boardMembersAndCeo;
    this.shareholders = shareholders;
    this.companyInfo = companyInfo;
    this.user = user;
    this.aiClient = aiClient;
    this.fileTypeFilter = new RadioFieldModel({
      value: "all",
      options: [...archiveFileTypeFilters],
      idFn: identity,
      labelFn: fileTypeFilterLabel,
    });
    this.signaturesFilter = new RadioFieldModel({
      value: "all",
      options: [...archiveSignaturesFilters],
      idFn: identity,
      labelFn: signaturesFilterLabel,
    });
    this.userRole = userRole;
    this.roleId = roleId;
    this.userPrefs = userPrefs;
  }

  openPermissionDialog() {
    if (this.rootFolderId) {
      this.dispatch({
        type: "open-change-permission-dialog",
        folderId: this.rootFolderId,
        title: this.rootFolderFromPath?.name ?? "",
      });
    }
  }

  get canEdit(): boolean {
    return allowArchiveEdit(this.rootFolder, this.userRole);
  }

  get rootFolderFromPath() {
    if (this.data?.path === undefined || this.data.path.length === 0) {
      return undefined;
    }
    return this.data.path[0];
  }

  sendForSigningFilePreview(entry: ArchiveEntry): FilePreviewProps {
    return {
      filename: entry.name,
      file: {
        url: `/api${archiveEntryUrl(this, entry)}`,
      },
      width: "sm",
    };
  }

  get rootFolderDescription() {
    return this.rootFolderFromPath?.folderDescription;
  }

  get rootFolderId() {
    return this.rootFolderFromPath?.id;
  }

  get folderId() {
    return fromQueryString(this.queryString).folderId ?? this.rootFolderId;
  }

  get entries(): ArchiveEntry[] {
    return this.data?.entries ?? [];
  }

  get dropEnabled(): boolean {
    return canUpload(this);
  }

  get fileUploadConfig(): FileUploadConfig {
    return this.data?.fileUploadConfig ?? emptyFileUploadConfig();
  }

  get showEmptyTrash(): boolean {
    return this.canEdit && this.rootFolder === "deleted";
  }

  emptyTrash() {
    return this.dispatch({
      type: "open-empty-trash-dialog",
    });
  }

  init() {
    this.state = { type: "loading" };
    this.dispatch(load(this));
  }

  handleEvent(event: ArchiveEvent) {
    switch (event.type) {
      case "update-permission-result":
        this.numberOfPermissions = event.numberOfPermissions;
        break;
      case "create-folder-result":
        if (isOk(event.result)) {
          this.dispatch(load(this));
        }
        break;
      case "delete-result":
        delete this.currentActionByEntry[event.entryId];
        if (isOk(event.result)) {
          remove(this.data?.entries ?? [], (entry) => entry.id === event.entryId);
          this.messageStore.showGeneralSuccess();
        } else {
          this.messageStore.showError(event.result.error);
        }
        this.state = { type: "ready" };
        break;
      case "download-result":
      case "download-signed-document-result":
        delete this.currentActionByEntry[event.entryId];
        if (isFailure(event.result)) {
          this.messageStore.showError(event.result.error);
        }
        break;
      case "get-destination-folders-result":
        delete this.currentActionByEntry[event.entry.id];
        if (isOk(event.result)) {
          this.openMoveDialog(event.entry, event.result.data.entries);
        } else {
          this.messageStore.showGeneralError();
        }
        break;
      case "get-signing-status-result":
      case "resend-for-signing-result":
        if (this.state.type === "signing-status") {
          this.state.dialogModel.setSigningStatusResult(event.result);
        }
        if (isOk(event.result)) {
          const signingStatus = event.result.data.documentSigningStatus;
          updateEntry(this.data?.entries ?? [], event.entryId, (entry) => {
            entry.signingStatus = signingStatus;
          });
        }
        break;
      case "load-result":
        if (isOk(event.result)) {
          this.data = event.result.data;
          this.numberOfPermissions = event.result.data.numberOfPermissions;
        } else {
          this.messageStore.showError(event.result.error);
        }
        this.state = { type: "ready" };
        break;
      case "move-result":
        this.state = { type: "ready" };
        if (isOk(event.result)) {
          const index = this.entries.indexOf(event.state.entry);
          if (index >= 0) {
            this.entries.splice(index, 1);
          }
          this.messageStore.showSuccess({
            key: "archive.move-success",
            params: {
              name: getEntryLabel(event.state.entry),
              destination: when(event.state.selectedDestinationFolder, getEntryLabel),
            },
          });
        } else {
          this.messageStore.showGeneralError();
        }
        break;
      case "rename-result":
        if (isOk(event.result)) {
          if (event.isRootFolder) {
            this.dispatch(load(this));
          } else {
            replaceEntry(this.data?.entries ?? [], event.result.data);
          }
        }
        break;
      case "restore-result":
        delete this.currentActionByEntry[event.entryId];
        if (isOk(event.result)) {
          this.dispatch({
            type: "navigate",
            ...restoreResponseToNavigationTarget(event.result.data),
          });
        } else {
          this.messageStore.showError(event.result.error);
        }
        break;
      case "send-for-signing-result":
        if (isOk(event.result)) {
          updateEntry(this.data?.entries ?? [], event.entryId, (entry) => {
            entry.signingStatus = DocumentSigningStatus.SENT_FOR_SIGNING;
          });
          this.dispatch({ type: "send-for-signing-success" });
        } else {
          this.messageStore.showError(event.result.error);
        }
        this.state = { type: "ready" };
        break;
      case "requires-subscription":
        this.state = { type: "requires-subscription" };
        this.currentActionByEntry = {};
        break;
      case "upload-result":
        if (isOk(event.result)) {
          this.dispatch(load(this));
        } else {
          this.messageStore.showError(event.result.error);
        }
        this.state = { type: "ready" };
        break;
      case "validate-files-for-upload-failure":
        if (isFileUploadValidationError(event.error)) {
          this.error = event.error;
        } else {
          this.error = { type: "message", message: { key: "archive.upload-error" } };
        }
        this.state = { type: "ready" };
        break;
      case "validate-files-for-upload-success":
        this.dispatch({ type: "upload", files: event.files, folderId: event.folderId });
        break;
    }
  }

  handleFilesChange(files: File[], fileUploadConfig: FileUploadConfig) {
    if (files.length && this.folderId !== undefined) {
      this.state = { type: "uploading" };
      this.dispatch({
        type: "validate-files-for-upload",
        folderId: this.folderId,
        fileUploadConfig,
        files,
      });
    }
  }

  handleDrop(files: File[]) {
    if (this.dropEnabled && this.state.type !== "drop-uploading") {
      if (!files.length || this.folderId === undefined) {
        this.state = { type: "ready" };
      } else {
        this.state = { type: "drop-uploading" };
        this.dispatch({
          type: "validate-files-for-upload",
          folderId: this.folderId,
          fileUploadConfig: this.fileUploadConfig,
          files,
        });
      }
    }
  }

  openCreateFolderDialog() {
    this.dispatch({
      type: "open-create-folder-dialog",
      existingEntries: getExistingNames(this.data?.entries ?? []),
      parentId: this.folderId,
    });
  }

  openPreviewDialog(entry: ArchiveEntry) {
    this.state = {
      type: "preview",
      entry,
      file: {
        url:
          entry.signingStatus === DocumentSigningStatus.SIGNING_COMPLETE
            ? `/api/document/view-signed-document/?ref=${entry.id}`
            : `/api${archiveEntryUrl(this, entry)}`,
      },
    };
  }

  closePreviewDialog() {
    this.state = { type: "ready" };
  }

  openRenameDialog(entry: ArchiveEntry) {
    this.dispatch({
      type: "open-rename-dialog",
      existingEntries: getExistingNames(this.data?.entries ?? []),
      isRootFolder: false,
      entryId: entry.id,
      name: entry.name,
    });
  }

  openMoveDialog(entry: ArchiveEntry, destinationFolders: DestinationFolder[]) {
    const isCustomRole = this.userRole && this.userRole.customRoleId !== "all";
    const destinations = destinationFolders.filter(
      (d) =>
        !isCustomRole ||
        (d.rootFolder && allowArchiveEdit(d.rootFolder, this.userRole)) ||
        (!d.rootFolder && allowArchiveEditForFolderType(d.type, this.userRole)),
    );
    this.state = createMoveState(entry, destinations);
  }

  selectMoveTarget(state: MoveState, entry: ArchiveEntry) {
    state.selectedDestinationFolder = entry;
  }

  submitMove(state: MoveState) {
    if (state.canSubmit && !state.isSubmitting) {
      state.isSubmitting = true;
      this.dispatch({ type: "move", state });
    }
  }

  cancelMove() {
    this.state = { type: "ready" };
  }

  openSendForSigningDialog(entry: ArchiveEntry) {
    this.state = {
      type: "send-for-signing",
      entry,
      form: new SignersForm({
        signers: [signer()],
        aiClient: this.aiClient,
        user: this.user,
        shareholders: this.shareholders,
        companyRoles: this.boardMembersAndCeo,
        emailBody: {
          key: "archive.send-for-signing.email-body",
          params: { senderName: getFullName(this.user), fileName: entry.name },
        },
      }),
      title: { translated: entry.name },
    };
  }

  addSigner() {
    if (this.state.type === "send-for-signing") {
      this.state.form.addSigner();
    }
  }

  get quickAddSignerItems() {
    if (this.state.type !== "send-for-signing") {
      return;
    }
    const form = this.state.form;
    return form.quickAddSignerItems;
  }

  commonSigningProperties(locale: Language) {
    if (this.state.type !== "send-for-signing") {
      return;
    }
    const form = this.state.form;
    return form.commonSigningProperties(locale);
  }

  addShareholders(shareholders: ExtendedShareholderDTO[]) {
    if (this.state.type === "send-for-signing") {
      return this.state.form.addShareholdersAsSigners(shareholders);
    }
  }

  removeSigner(signer: SignerModel) {
    if (this.state.type === "send-for-signing") {
      this.state.form.removeSigner(signer);
    }
  }

  canRemoveSigner(): boolean {
    return (
      this.state.type === "send-for-signing" &&
      (this.state.form.items.length > 1 || !this.state.form.items[0].isEmpty)
    );
  }

  cancelSendForSigning() {
    this.state = { type: "ready" };
  }

  submitSendForSigning() {
    if (this.state.type === "send-for-signing" && this.state.form.validate()) {
      const sendSealedCopyToOthers = this.state.form.sendSealedCopyToOtherRecipients;
      this.dispatch({
        type: "send-for-signing",
        entryId: this.state.entry.id,
        signers: this.state.form.toSigners(),
        emailBody: this.state.form.emailBody.textModels[0].model.inputValue,
        signingSettings: {
          sendSealedCopyTo: this.state.form.sendSealedCopyTo.selected.map((d) =>
            mapSendSealedCopyTo(d, sendSealedCopyToOthers),
          ),
          batchEmails: this.state.form.batchEmails.value,
        },
      });
      this.state.isSending = true;
    }
  }

  openSigningStatusDialog(entry: ArchiveEntry) {
    this.state = {
      type: "signing-status",
      dialogModel: new ViewDocumentSigningStatusDialogModel({
        documentId: entry.id,
        documentType: entry.type,
        title: { translated: entry.name },
        closeDialog: this.closeSigningStatusDialog,
        getSigningStatus: () => this.dispatch({ type: "get-signing-status", entryId: entry.id }),
        resendDocumentForSigning: (signedDocumentId, data) =>
          this.dispatch({ type: "resend-for-signing", entryId: entry.id, signedDocumentId, data }),
        downloadSignedDocument: () =>
          this.dispatch({ type: "download-signed-document", entryId: entry.id }),
        confirmDeleteSignatureRequest: () =>
          this.dispatch({
            type: "delete-signature-request",
            entryId: entry.id,
            projectId: entry.projectId,
          }),
      }),
    };
    this.state.dialogModel.init();
  }

  closeSigningStatusDialog() {
    this.state = { type: "ready" };
  }

  openConfirmDeleteForeverDialog(entry: ArchiveEntry) {
    this.state = { type: "confirm-delete-forever", entry };
  }

  openDeleteSignatureRequestDialog(entry: ArchiveEntry, onConfirmCancel?: ArchiveEvent) {
    this.state = { type: "confirm-delete-signature-request", entry, onConfirmCancel };
  }

  handleDeleteSignatureRequest() {
    if (this.state.type === "confirm-delete-signature-request") {
      this.state.loading = true;
      this.dispatch({
        type: "delete-signature-request",
        entryId: this.state.entry.id,
        onConfirmCancel: this.state.onConfirmCancel,
      });
    }
  }

  closeDialog() {
    this.state = { type: "ready" };
  }

  handleConfirmDeleteForever() {
    if (this.state.type === "confirm-delete-forever") {
      this.state.loading = true;
      this.dispatch({ type: "delete", entryId: this.state.entry.id, permanent: true });
    }
  }

  handleCancelDeleteForever() {
    this.state = { type: "ready" };
  }

  handleCloseRequiresSubscriptionDialog() {
    this.state = { type: "ready" };
  }

  handleSubmitRequiresSubscriptionDialog() {
    this.dispatch({ type: "navigate-to-payment" });
  }

  getEntryMenuItems(entry: ArchiveEntry, t: TFunction): DropdownMenuItems {
    return getEntryActions(this.rootFolder, entry, this.entries, this.userRole).map((action) =>
      toEntryMenuItem({ action, t, onSelect: () => this.performEntryAction(action, entry) }),
    );
  }

  performEntryAction(action: ArchiveEntryAction, entry: ArchiveEntry) {
    logDebug("archive::admin-archive::performAction", action, toJS(entry));
    switch (action) {
      case "delete-signing-request":
        this.openDeleteSignatureRequestDialog(entry);
        break;
      case "delete":
        if (isSigningInProgress(entry)) {
          this.openDeleteSignatureRequestDialog(entry, { type: "delete", entryId: entry.id });
        } else {
          this.currentActionByEntry[entry.id] = action;
          this.dispatch({ type: "delete", entryId: entry.id });
        }
        break;
      case "delete-forever":
        this.openConfirmDeleteForeverDialog(entry);
        break;
      case "download":
        this.currentActionByEntry[entry.id] = action;
        this.dispatch({
          type:
            entry.signingStatus === DocumentSigningStatus.SIGNING_COMPLETE
              ? "download-signed-document"
              : "download",
          entryId: entry.id,
        });
        break;
      case "edit-in-dbot":
        if (entry.projectId && entry.linkTargetId) {
          this.dispatch({
            type: "navigate-to-dbot",
            projectId: entry.projectId,
            documentId: entry.linkTargetId,
          });
        }
        break;
      case "move":
        this.currentActionByEntry[entry.id] = action;
        this.dispatch({ type: "get-destination-folders", entry });
        break;
      case "open":
        this.dispatch({
          type: "navigate",
          rootFolder: this.rootFolder,
          folderId: entry.id,
          roleId: this.roleId,
        });
        break;
      case "open-meeting":
        if (entry.projectId) {
          this.dispatch({
            type: "navigate-to-board-meeting",
            projectId: entry.projectId,
            view:
              entry.linkTargetType === ArchiveDocumentType.NOTICE_BOARD_MEETING
                ? "notice"
                : "minutes",
          });
        }
        break;
      case "preview":
        this.openPreviewDialog(entry);
        break;
      case "rename":
        this.openRenameDialog(entry);
        break;
      case "restore":
        this.currentActionByEntry[entry.id] = action;
        this.dispatch({ type: "restore", entryId: entry.id });
        break;
      case "send-for-signing":
        this.openSendForSigningDialog(entry);
        break;
      case "see-signing-status":
        this.openSigningStatusDialog(entry);
        break;
    }
  }

  isPerformingEntryAction(entry: ArchiveEntry): boolean {
    return this.currentActionByEntry[entry.id] !== undefined;
  }

  handleBreadcrumbClick(id: string) {
    if (id === this.rootFolder) {
      this.dispatch({ type: "navigate", rootFolder: this.rootFolder });
    } else {
      this.dispatch({
        type: "navigate",
        rootFolder: this.rootFolder,
        folderId: id,
      });
    }
  }

  navigateToNewProject() {
    this.dispatch({ type: "navigate-to-new-project" });
  }
}

export class NavigationModel {
  readonly dispatch: ArchiveDispatch;
  rootFolderCounts: Record<string, number | undefined> = {};
  showRootFoldersMobile = false;
  currentRootFolderId?: string;
  currentRootFolderType?: ArchiveRootFolderType;
  rootFolders?: ArchiveRootFolder[];
  roleId?: string;

  constructor(dispatch: ArchiveDispatch) {
    makeAutoObservable(this, { dispatch: false }, { autoBind: true });
    this.dispatch = dispatch;
  }

  initCurrentRootFolderId(
    rootFolderType?: ArchiveDocumentType,
    rootFolderId?: string,
    roleId?: string,
  ) {
    this.currentRootFolderType = rootFolderType
      ? documentTypeToRootFolder(rootFolderType)
      : ArchiveRootFolderType.DELETED;
    this.currentRootFolderId = rootFolderId ?? "";
    this.roleId = roleId;
  }

  updateRootFolders(rootFolders: Array<ArchiveRootFolder> | undefined) {
    this.rootFolders = rootFolders;
  }

  get predefinedFolders() {
    return this.rootFolders?.filter(
      (folder) => folder.folderType !== ArchiveRootFolderType.CUSTOM_ARCHIVE,
    );
  }

  get customArchives() {
    return this.rootFolders?.filter(
      (folder) => folder.folderType === ArchiveRootFolderType.CUSTOM_ARCHIVE,
    );
  }

  navigateTo(rootFolder: ArchiveRootFolder) {
    this.showRootFoldersMobile = false;
    this.dispatch({
      type: "navigate",
      rootFolder: rootFolder.folderType,
      folderId: rootFolder.folderId ? rootFolder.folderId : undefined,
      roleId: this.roleId,
    });
  }

  openCreateFolderDialog() {
    this.dispatch({
      type: "open-create-folder-dialog",
      existingEntries: getExistingNames(
        this.rootFolders?.map((d) => {
          return { id: d.folderId, name: d.folderTitle ?? "" };
        }) ?? [],
      ),
    });
  }

  toggleRootFoldersMobile() {
    this.showRootFoldersMobile = !this.showRootFoldersMobile;
  }
}

export const filterByPermission = (
  data: ArchiveRootFolder[],
  userRole: CustomRoleForUser,
): ArchiveRootFolder[] => {
  return data.filter((folder) => {
    switch (folder.folderType) {
      case ArchiveRootFolderType.BOARD_ARCHIVE:
        return userRole.allowBoardArchiveView;
      case ArchiveRootFolderType.SHAREHOLDER_ARCHIVE:
        return userRole.allowShareholderArchiveView;
      case ArchiveRootFolderType.ADMIN_ARCHIVE:
        return userRole.allowAdminArchiveView;
      case ArchiveRootFolderType.CUSTOM_ARCHIVE:
        return userRole.allowCustomArchiveView;
      default:
        return false;
    }
  });
};

export class ArchiveStore {
  readonly history: History;
  readonly organisationNumber: string;
  readonly apiClient: ArchiveApiClient;
  readonly documentApiClient: DocumentApiClient;
  readonly hasRequiredSubscription: boolean;
  readonly messageStore: AlertMessageStore;
  dialogStore: DialogStore;
  rootFolderModel?: RootFolderModel;
  navigationModel: NavigationModel;
  data?: ListArchiveResponse;
  rootFolders?: ArchiveRootFolder[];
  boardMembersAndCeo: CompanyRole[];
  shareholders: ExtendedShareholderDTO[];
  companyInfo?: CompanyInfo;
  aiClient: AIClient;
  state: ArchiveDialogState = { type: "none" };

  initError = false;
  onEnterPayment: () => void;
  user: User | null;
  userPrefs: UserPrefs;

  constructor(
    history: History,
    http: AxiosInstance,
    dialogStore: DialogStore,
    messageStore: AlertMessageStore,
    organisationNumber: string,
    hasRequiredSubscription: boolean,
    onEnterPayment: () => void,
    shareholders: ExtendedShareholderDTO[],
    readonly userRole: CustomRoleForUser,
    userPrefs: UserPrefs,
    companyInfo?: CompanyInfo,
    user: User | null = null,
  ) {
    this.onEnterPayment = onEnterPayment;
    makeAutoObservable(this, { history: false }, { autoBind: true });
    this.boardMembersAndCeo = sortedBoardAndCEO(companyInfo?.roles ?? []);
    this.shareholders = shareholders;
    this.companyInfo = companyInfo;
    this.history = history;
    this.organisationNumber = organisationNumber;
    this.apiClient = new ArchiveApiClient(http, organisationNumber);
    this.documentApiClient = new DocumentApiClient(http, organisationNumber);
    this.navigationModel = new NavigationModel(this.dispatch);
    this.hasRequiredSubscription = hasRequiredSubscription;
    this.user = user;
    this.aiClient = new AIClient(http, organisationNumber);
    this.messageStore = messageStore;
    this.dialogStore = dialogStore;
    this.userPrefs = userPrefs;
  }

  get rootFolder() {
    if (this.data?.path === undefined || this.data.path.length === 0) {
      return undefined;
    }
    return this.data.path[0];
  }

  closeArchiveDialog() {
    switch (this.state.type) {
      case "rename":
      case "create-folder": {
        if (!this.state.submitting) {
          this.state = { type: "none" };
        }
        break;
      }
      default:
        this.state = { type: "none" };
    }
  }

  openCustomArchiveRenameDialog() {
    const rootFolder = this.findRootFolder(
      ArchiveRootFolderType.CUSTOM_ARCHIVE,
      this.rootFolderModel?.rootFolderId,
    );
    if (rootFolder !== undefined) {
      this.dispatch({
        type: "open-rename-dialog",
        existingEntries: getExistingNames(
          this.rootFolders?.map((d) => {
            return { id: d.folderId, name: d.folderTitle ?? "" };
          }) ?? [],
          rootFolder.folderId,
        ),
        isRootFolder: true,
        entryId: rootFolder.folderId,
        name: rootFolder.folderTitle ?? "",
        description: rootFolder.folderDescription,
      });
    }
  }

  findRootFolder(type: ArchiveRootFolderType, id?: string) {
    return findRootFolder(this.rootFolders ?? [], type, id);
  }

  get rootFolderId(): string {
    return (
      this.findRootFolder(
        this.rootFolderModel?.rootFolder ?? ArchiveRootFolderType.ADMIN_ARCHIVE,
        this.rootFolderModel?.rootFolderId,
      )?.folderId ?? ""
    );
  }

  dispatch(event: ArchiveEvent) {
    logDebug("archive::dispatch", event);
    const source = this.rootFolderModel;
    switch (event.type) {
      case "open-create-folder-dialog":
        this.state = {
          type: "create-folder",
          parentId: event.parentId,
          name: new StringFieldModel({
            required: true,
            validator: (s) => validateFilename(event.existingEntries, s),
          }),
          description: new LocalizedTextEditorModel({
            texts: [
              { text: "", language: Language.NO, translationOption: { autoTranslate: false } },
            ],
            isReadOnly: () => false,
            label: { key: "transactions.description" },
            aiClient: this.aiClient,
          }),
          showDescription: new BooleanFieldModel(),
        };
        break;
      case "open-rename-dialog":
        this.state = {
          type: "rename",
          isRootFolder: event.isRootFolder,
          entryId: event.entryId,
          name: new StringFieldModel({
            value: event.name,
            required: true,
            validator: (s) => validateFilename(event.existingEntries, s),
          }),
          description: new LocalizedTextEditorModel({
            texts: [
              {
                text: event.description ?? "",
                language: Language.NO,
                translationOption: { autoTranslate: false },
              },
            ],
            isReadOnly: () => false,
            label: { key: "transactions.description" },
            aiClient: this.aiClient,
          }),
        };
        break;
      case "open-delete-archive-forever":
        this.state = createDeleteArchiveState(event.entryId, event.name);
        break;
      case "open-empty-trash-dialog":
        this.state = createEmptyTrashState();
        break;
      case "delete-signature-request": {
        const request = event.projectId
          ? this.apiClient.reopenLockedDocument(event.entryId)
          : this.apiClient.cancelSignatureRequest(event.entryId);
        request
          .then(() => {
            if (event.onConfirmCancel) this.dispatch(event.onConfirmCancel);
            else {
              this.rootFolderModel?.closeDialog();
              const entry = this.rootFolderModel?.entries?.find((x) => x.id === event.entryId);
              if (entry) {
                entry.signingStatus = DocumentSigningStatus.UNKNOWN;
              }
              this.messageStore?.showSuccess({ key: "general-success" });
            }
          })
          .catch(() => {
            this.rootFolderModel?.closeDialog();
            this.messageStore?.showError({ key: "general-error" });
          });
        break;
      }
      case "create-folder":
        if (
          this.state.type === "create-folder" &&
          !this.state.submitting &&
          validateCreateFolderState(this.state)
        ) {
          const isRootFolder = this.state.parentId === undefined;
          this.state.submitting = true;
          this.apiClient
            .createFolder({
              name: this.state.name.inputValue,
              parentId: this.state.parentId,
              description: this.state.description.textModels[0].model.isEmpty
                ? undefined
                : this.state.description.textModels[0].model.inputValue,
            })
            .then((folder) => {
              if (isRootFolder) {
                this.dispatch({ type: "load-root-folders" });
              } else {
                source?.handleEvent({
                  type: "create-folder-result",
                  result: ok(folder),
                });
              }
              this.state = { type: "none" };
              this.dispatch({ type: "load-folder-counts" });
            })
            .catch(() => {
              this.state = { type: "none" };
              this.messageStore?.showError({ key: "archive.create-folder-error" });
            });
        }
        break;
      case "delete-archive":
      case "delete":
        if (event.type === "delete-archive") {
          if (this.state.type === "confirm-delete-archive-forever") {
            this.state.loading = true;
          } else {
            break;
          }
        }
        this.apiClient
          .delete(event)
          .then(() => {
            if (event.type === "delete-archive") {
              this.dispatch({ type: "load-root-folders" });
              this.state = { type: "none" };
              this.dispatch({
                type: "navigate",
                rootFolder: ArchiveRootFolderType.ADMIN_ARCHIVE,
              });
            } else {
              source?.handleEvent({
                type: "delete-result",
                entryId: event.entryId,
                result: ok(),
              });
              this.dispatch({ type: "load-folder-counts" });
            }
          })
          .catch(() => {
            source?.handleEvent({
              type: "delete-result",
              entryId: event.entryId,
              result: failure({ key: "archive.delete-error" }),
            });
          });
        break;

      case "empty-trash":
        if (this.state.type === "confirm-empty-trash") {
          this.state.loading = true;
        } else {
          break;
        }
        this.apiClient
          .emptyTrash()
          .then(() => {
            source?.handleEvent({
              type: "empty-trash-result",
              result: ok(),
            });
            this.state = { type: "none" };
            this.rootFolderModel?.dispatch({
              type: "load",
              rootFolder: ArchiveRootFolderType.DELETED,
            });
            this.messageStore.showSuccess({ key: "archive.empty-trash-success" });
          })
          .catch(() => {
            source?.handleEvent({
              type: "empty-trash-result",
              result: failure({ key: "archive.delete-error" }),
            });
            this.messageStore.showError({ key: "archive.empty-trash-error" });
          });
        break;

      case "download":
        if (!this.hasRequiredSubscription) {
          handleSubscriptionRequired(source);
        } else {
          const entry = this.rootFolderModel?.entries?.find((x) => x.id === event.entryId);
          if (!entry) {
            return;
          }
          this.apiClient
            .download(entry)
            .then((res) => {
              downloadFile(res.data, getFilenameFromHeaders(res.headers));
              source?.handleEvent({
                type: "download-result",
                entryId: event.entryId,
                result: ok(),
              });
            })
            .catch(() => {
              source?.handleEvent({
                type: "download-result",
                entryId: event.entryId,
                result: failure({ key: "archive.download-error" }),
              });
            });
        }
        break;

      case "download-signed-document":
        if (!this.hasRequiredSubscription) {
          handleSubscriptionRequired(source);
        } else {
          downloadPdf(`/document/view-signed-document/?ref=${event.entryId}`, this.apiClient.http)
            .then(() =>
              source?.handleEvent({
                type: "download-signed-document-result",
                entryId: event.entryId,
                result: ok(),
              }),
            )
            .catch(() => {
              source?.handleEvent({
                type: "download-signed-document-result",
                entryId: event.entryId,
                result: failure({ key: "archive.download-error" }),
              });
            });
        }
        break;

      case "get-signing-status":
        this.documentApiClient
          .getSigningStatus({ documentId: event.entryId })
          .then((res) => {
            source?.handleEvent({
              type: "get-signing-status-result",
              entryId: event.entryId,
              result: ok(res),
            });
          })
          .catch(() => {
            source?.handleEvent({
              type: "get-signing-status-result",
              entryId: event.entryId,
              result: failure({ key: "archive.get-signing-status-error" }),
            });
          });
        break;

      case "init": {
        const roleId = fromQueryString(event.queryString).roleId;
        if (!isRootFolderType(event.rootFolder)) {
          let rootFolder: ArchiveRootFolderType | undefined = undefined;
          if (this.userRole.allowAdminArchiveView) {
            rootFolder = ArchiveRootFolderType.ADMIN_ARCHIVE;
          } else if (this.userRole.allowBoardArchiveView) {
            rootFolder = ArchiveRootFolderType.BOARD_ARCHIVE;
          } else if (this.userRole.allowShareholdersView) {
            rootFolder = ArchiveRootFolderType.SHAREHOLDER_ARCHIVE;
          }
          if (rootFolder) {
            this.dispatch({
              type: "navigate",
              rootFolder,
              roleId,
              replace: true,
            });
          } else {
            logError("Invalid access for user in ", this.userRole);
          }
        } else if (!this.rootFolders) {
          this.dispatch({ type: "load-root-folders", onSuccess: event });
        } else {
          this.rootFolderModel = new RootFolderModel(
            this.messageStore,
            this.dispatch,
            event.rootFolder,
            event.queryString,
            this.organisationNumber,
            this.aiClient,
            this.userRole,
            this.userPrefs,
            this.boardMembersAndCeo,
            this.shareholders,
            this.companyInfo,
            this.user,
            roleId,
          );
          this.rootFolderModel.init();
        }
        break;
      }

      case "get-destination-folders":
        this.apiClient
          .getDestinationFolders(event.entry.id)
          .then((res) =>
            source?.handleEvent({
              type: "get-destination-folders-result",
              entry: event.entry,
              result: ok(res),
            }),
          )
          .catch(() => {
            source?.handleEvent({
              type: "get-destination-folders-result",
              entry: event.entry,
              result: failure(),
            });
          });
        break;

      case "load":
        this.apiClient
          .list(toListArchiveFilter({ ...event, rootFolderId: this.rootFolderId }))
          .then((res) => {
            this.data = res;
            this.navigationModel.initCurrentRootFolderId(
              this.rootFolder?.type,
              this.rootFolder?.id,
              event.roleId,
            );
            source?.handleEvent({
              type: "load-result",
              rootFolder: event.rootFolder,
              folderId: event.folderId,
              result: ok(res),
            });
            this.dispatch({ type: "load-folder-counts" });
          })
          .catch(() => {
            source?.handleEvent({
              type: "load-result",
              rootFolder: event.rootFolder,
              folderId: event.folderId,
              result: failure({ key: "archive.fetchError" }),
            });
          });
        break;

      case "load-root-folders":
        this.apiClient
          .loadRootFolders()
          .then((res) => {
            this.rootFolders = filterByPermission(res, this.userRole);
            this.navigationModel.updateRootFolders(this.rootFolders);
            if (event.onSuccess) {
              this.dispatch(event.onSuccess);
            }
          })
          .catch(() => {
            this.initError = true;
          });
        break;

      case "move":
        this.apiClient
          .move({
            entryId: event.state.entry.id,
            destinationId: event.state.selectedDestinationFolder?.id,
          })
          .then(() =>
            source?.handleEvent({ type: "move-result", state: event.state, result: ok() }),
          )
          .catch(() => {
            return source?.handleEvent({
              type: "move-result",
              state: event.state,
              result: failure(),
            });
          });
        break;
      case "navigate": {
        const url =
          adminArchive({ orgNo: this.organisationNumber }) +
          `/${event.rootFolder}${toQueryString({ folderId: event.folderId, roleId: event.roleId })}`;
        if (event.replace) {
          this.history.replace(url);
        } else {
          this.history.push(url);
        }
        break;
      }

      case "navigate-to-board-meeting":
        this.history.push(previewBoardMeetingForAdmin(this.organisationNumber, event.projectId));
        break;

      case "navigate-to-dbot":
        navigateToProject({
          orgNo: this.organisationNumber,
          projectId: event.projectId,
          history: this.history,
          state: { type: "project-navigation-state", documentId: event.documentId },
        });
        break;

      case "navigate-to-new-project":
        navigateToProjectOverview(this.organisationNumber, this.history);
        break;

      case "navigate-to-payment":
        this.onEnterPayment();
        break;

      case "rename":
        if (
          this.state.type === "rename" &&
          !this.state.submitting &&
          validateRenameState(this.state)
        ) {
          const isRootFolder = this.state.isRootFolder;
          const name = this.state.name.inputValue;
          this.state.submitting = true;
          this.apiClient
            .rename({
              entryId: this.state.entryId,
              name,
              description: this.state.description.textModels[0].model.isEmpty
                ? undefined
                : this.state.description.textModels[0].model.inputValue,
            })
            .then((updatedEntry) => {
              if (isRootFolder) {
                this.dispatch({ type: "load-root-folders" });
              }
              source?.handleEvent({
                type: "rename-result",
                isRootFolder: isRootFolder,
                result: ok(updatedEntry),
              });

              this.state = { type: "none" };
            })
            .catch(() => {
              this.messageStore.showError({ key: "general-error" });
            });
        }
        break;

      case "resend-for-signing":
        this.documentApiClient
          .resendForSigning(
            { documentId: event.entryId, signedDocumentId: event.signedDocumentId },
            event.data,
          )
          .then((data) => {
            source?.handleEvent({
              type: "resend-for-signing-result",
              entryId: event.entryId,
              signedDocumentId: event.signedDocumentId,
              result: ok(data),
            });
          })
          .catch(() => {
            source?.handleEvent({
              type: "resend-for-signing-result",
              entryId: event.entryId,
              signedDocumentId: event.signedDocumentId,
              result: failure({ key: "archive.resend-for-signing-error" }),
            });
          });
        break;

      case "restore":
        this.apiClient
          .restore(event)
          .then((entry) =>
            source?.handleEvent({
              type: "restore-result",
              entryId: event.entryId,
              result: ok(entry),
            }),
          )
          .catch(() => {
            source?.handleEvent({
              type: "restore-result",
              entryId: event.entryId,
              result: failure({ key: "archive.restore-error" }),
            });
          });
        break;

      case "send-for-signing":
        if (!this.hasRequiredSubscription) {
          handleSubscriptionRequired(source);
        } else {
          this.documentApiClient
            .sendUploadedDocumentForSigning({
              documentId: event.entryId,
              signers: event.signers.map(mapToSigners),
              emailBody: event.emailBody,
              signingSettings: event.signingSettings,
            })
            .then(() => {
              source?.handleEvent({
                type: "send-for-signing-result",
                entryId: event.entryId,
                result: ok(),
              });
            })
            .catch(() => {
              source?.handleEvent({
                type: "send-for-signing-result",
                entryId: event.entryId,
                result: failure({ key: "archive.send-for-signing-error" }),
              });
            });
        }
        break;

      case "upload":
        if (!this.hasRequiredSubscription) {
          handleSubscriptionRequired(source);
        } else {
          this.apiClient
            .upload({ folderId: event.folderId, files: event.files })
            .then((res) => {
              source?.handleEvent({ type: "upload-result", result: ok(res) });
              this.dispatch({ type: "load-folder-counts" });
            })
            .catch(() => {
              source?.handleEvent({
                type: "upload-result",
                result: failure({ key: "archive.upload-error" }),
              });
            });
        }
        break;

      case "validate-files-for-upload":
        validateFiles(event.files, event.fileUploadConfig)
          .then((files) =>
            source?.handleEvent({ ...event, type: "validate-files-for-upload-success", files }),
          )
          .catch((error) => {
            logError(error, "Invalid file for upload");
            source?.handleEvent({ ...event, type: "validate-files-for-upload-failure", error });
          });
        break;

      case "send-for-signing-success":
        if (isAlertDismissed(this.userPrefs, "signingImprovements")) {
          this.messageStore.showSuccess(
            { key: "archive.send-for-signing-success" },
            { autoHide: true },
          );
        } else {
          this.dialogStore.openDialog({
            type: "submit-for-signing-success",
            orgNo: this.organisationNumber,
          });
        }
        break;

      case "open-change-permission-dialog":
        this.dialogStore.openChangePermissionDialog(
          event.title,
          toDocumentReference(event.folderId),
          (permissions: ObjectPermission[]) =>
            source?.handleEvent({
              type: "update-permission-result",
              numberOfPermissions: permissions.length,
            }),
        );
        break;

      default:
        logDebug("archive:dispatch: Unhandled event", event);
        break;
    }
  }
}

export function findRootFolder(
  rootFolders: Array<ArchiveRootFolder>,
  rootFolder: ArchiveRootFolderType,
  rootFolderId?: string,
) {
  return rootFolders.find(
    (f) =>
      f.folderType === rootFolder && (rootFolderId === undefined || f.folderId === rootFolderId),
  );
}

export function validateFilename(existingNames: string[], name: string): TMessage | undefined {
  if (notUnique(existingNames, name)) {
    return { key: "archive.validation.not-unique-name" };
  }
  if (!/^[0-9A-Za-zÀ-ÖØ-öø-ÿ (),._-]+$/.test(name)) {
    return { key: "archive.validation.invalid-characters" };
  }
}

function handleSubscriptionRequired(source: RootFolderModel | undefined) {
  source?.handleEvent({ type: "requires-subscription" });
}

function notUnique(existingNames: string[], name: string) {
  return existingNames.some(
    (existingName) => existingName.trim().toLocaleLowerCase() === name.trim().toLocaleLowerCase(),
  );
}

export function getExistingNames(
  entries: { id?: string; name: string }[],
  currentEntryId?: string,
): string[] {
  return entries
    .filter((entry) => !currentEntryId || entry.id !== currentEntryId)
    .map((entry) => entry.name);
}

function validateCreateFolderState(state: CreateFolderState): boolean {
  state.name.validate();
  return state.name.isValid;
}

function validateRenameState(state: RenameState): boolean {
  state.name.validate();
  return state.name.isValid;
}

export function replaceEntry(entries: ArchiveEntry[], entry: ArchiveEntry) {
  const index = entries.findIndex((x) => x.id === entry.id);
  if (index >= 0) {
    entries.splice(index, 1, entry);
  }
}

export function updateEntry(
  entries: ArchiveEntry[],
  id: string,
  action: (entry: ArchiveEntry) => void,
) {
  const index = entries.findIndex((x) => x.id === id);
  if (index >= 0) {
    action(entries[index]);
  }
}

const allowArchiveEditForFolderType = (
  rootFolder: ArchiveDocumentType,
  userRole: CustomRoleForUser,
): boolean => {
  switch (rootFolder) {
    case ArchiveDocumentType.BOARD_ARCHIVE_FOLDER:
      return userRole.allowBoardArchiveEdit ?? false;
    case ArchiveDocumentType.SHAREHOLDER_ARCHIVE_FOLDER:
      return userRole.allowShareholderArchiveEdit ?? false;
    case ArchiveDocumentType.CUSTOM_ARCHIVE_FOLDER:
      return userRole.allowCustomArchiveEdit ?? false;
    case ArchiveDocumentType.ADMIN_ARCHIVE_FOLDER:
      return userRole.allowAdminArchiveEdit ?? false;
    default:
      return false;
  }
};

const allowArchiveEdit = (
  rootFolder: ArchiveRootFolderType,
  userRole: CustomRoleForUser,
): boolean => {
  switch (rootFolder) {
    case ArchiveRootFolderType.BOARD_ARCHIVE:
      return userRole.allowBoardArchiveEdit ?? true;
    case ArchiveRootFolderType.SHAREHOLDER_ARCHIVE:
      return userRole.allowShareholderArchiveEdit ?? true;
    case ArchiveRootFolderType.ADMIN_ARCHIVE:
      return userRole.allowAdminArchiveEdit ?? true;
    case ArchiveRootFolderType.CUSTOM_ARCHIVE:
      return userRole.allowCustomArchiveEdit ?? true;
    default:
      return true;
  }
};

const folderActions = (
  entry: ArchiveEntry,
  rootFolder: ArchiveRootFolderType,
  userRole: CustomRoleForUser,
): ArchiveEntryAction[] => {
  const allowEdit = allowArchiveEdit(rootFolder, userRole);
  return compact([
    "open",
    allowEdit && !entry.isReadonly && "move",
    allowEdit && !entry.isReadonly && "rename",
    allowEdit && !entry.isReadonly && "delete",
  ]);
};

const isPdf = (entry: ArchiveEntry) => isExtensionPdf(entry.name);

const isSentForSigning = (entry: ArchiveEntry) =>
  entry.signingStatus &&
  [
    DocumentSigningStatus.SIGNING_IN_PROGRESS,
    DocumentSigningStatus.SENT_FOR_SIGNING,
    DocumentSigningStatus.SIGNING_COMPLETE,
  ].includes(entry.signingStatus);

const fileActions = (
  entry: ArchiveEntry,
  entries: ArchiveEntry[],
  rootFolder: ArchiveRootFolderType,
  userRole: CustomRoleForUser,
): ArchiveEntryAction[] => {
  const canEdit = allowArchiveEdit(rootFolder, userRole);
  switch (entry.type) {
    case ArchiveDocumentType.LINK:
      switch (entry.linkTargetType) {
        case ArchiveDocumentType.BOARD_MEETING:
          return compact([
            userRole.allowDbot &&
              entries.some((e) => e.linkTargetType === ArchiveDocumentType.NOTICE_BOARD_MEETING) &&
              "open-meeting",
            userRole.allowDbot && "edit-in-dbot",
          ]);
        case ArchiveDocumentType.NOTICE_BOARD_MEETING:
          return compact([
            userRole.allowDbot && "open-meeting",
            userRole.allowDbot && "edit-in-dbot",
          ]);
        default:
          return ["preview", "download"];
      }
    default:
      return compact([
        "preview",
        "download",
        isPdf(entry) &&
          entry.signingStatus === DocumentSigningStatus.UNKNOWN &&
          canEdit &&
          "send-for-signing",
        isSentForSigning(entry) && canEdit && "see-signing-status",
        canEdit && isSigningInProgress(entry) && "delete-signing-request",
        !entry.isReadonly && canEdit && "move",
        !entry.isReadonly && canEdit && "rename",
        !entry.isReadonly && canEdit && "delete",
      ]);
  }
};

export function getEntryActions(
  rootFolder: ArchiveRootFolderType,
  entry: ArchiveEntry,
  entries: ArchiveEntry[],
  userRole: CustomRoleForUser,
): ArchiveEntryAction[] {
  switch (rootFolder) {
    case ArchiveRootFolderType.DELETED:
      return compact([
        "restore",
        entry.type === ArchiveDocumentType.FILE && "preview",
        entry.type === ArchiveDocumentType.FILE && "download",
        "delete-forever",
      ]);
    default:
      return entry.isFolder
        ? folderActions(entry, rootFolder, userRole)
        : fileActions(entry, entries, rootFolder, userRole);
  }
}

function getActionColor(action: ArchiveEntryAction) {
  switch (action) {
    case "delete-forever":
      return "error";
  }
}

export function getPrimaryEntryAction(
  rootFolder: ArchiveRootFolderType,
  entry: ArchiveEntry,
  entries: ArchiveEntry[],
  userRole: CustomRoleForUser,
): ArchiveEntryAction | undefined {
  switch (entry.type) {
    case ArchiveDocumentType.LINK:
      switch (entry.linkTargetType) {
        case ArchiveDocumentType.BOARD_MEETING:
        case ArchiveDocumentType.NOTICE_BOARD_MEETING:
          return getEntryActions(rootFolder, entry, entries, userRole)[0];
        default:
          return "preview";
      }
    default:
      return entry.isFolder
        ? rootFolder === ArchiveRootFolderType.DELETED
          ? undefined
          : "open"
        : "preview";
  }
}

export function getRootFolderDescription(
  rootFolder: ArchiveRootFolderType,
  rootFolderDescription?: string,
  companyInfo?: CompanyInfo,
): TMessage | undefined {
  switch (rootFolder) {
    case ArchiveRootFolderType.DELETED:
      return { key: "archive.trash-description" };
    case ArchiveRootFolderType.SHAREHOLDER_ARCHIVE:
      return { key: "archive.shareholderCommunication.description" };
    case ArchiveRootFolderType.BOARD_ARCHIVE:
      return { key: "archive.board-archive-description" };
    case ArchiveRootFolderType.ADMIN_ARCHIVE:
      return {
        key: "archive.admin-archive-description",
        params: { companyName: companyInfo?.company.name },
      };
    case ArchiveRootFolderType.CUSTOM_ARCHIVE:
      return rootFolderDescription ? { translated: rootFolderDescription } : undefined;
  }
}

export function getBreadcrumbs(
  f: ArchiveRootFolderType,
  path?: ArchivePathNode[],
): { id: string; name: TMessage }[] {
  return (path ?? []).map((p) => ({ id: p.id, name: getEntryLabel(p) }));
}

export function getRootFolderLabel(f: ArchiveRootFolderType, customLabel?: string): TMessage {
  switch (f) {
    case ArchiveRootFolderType.BOARD_ARCHIVE:
      return { key: "archive.board-archive" };
    case ArchiveRootFolderType.GENERAL_MEETINGS:
      return { key: "meetings.general-meetings" };
    case ArchiveRootFolderType.SEND_FOR_SIGNING:
      return { key: "archive.sent-for-signing" };
    case ArchiveRootFolderType.ADMIN_ARCHIVE:
      return { key: "archive.admin-archive" };
    case ArchiveRootFolderType.SHAREHOLDER_ARCHIVE:
      return { key: "archive.shareholder-archive" };
    case ArchiveRootFolderType.DELETED:
      return { key: "archive.trash" };
    case ArchiveRootFolderType.CUSTOM_ARCHIVE:
      return customLabel ? { translated: customLabel } : { key: "archive.custom-archive" };
  }
}

export function getSentForSigningTitle(parentTitle: string): TMessage {
  switch (parentTitle) {
    case "admin_archive_folder":
      return getRootFolderLabel(ArchiveRootFolderType.ADMIN_ARCHIVE);
    case "board_archive_folder":
      return getRootFolderLabel(ArchiveRootFolderType.BOARD_ARCHIVE);
    case "shareholder_archive_folder":
      return getRootFolderLabel(ArchiveRootFolderType.SHAREHOLDER_ARCHIVE);
    default:
      return { translated: parentTitle };
  }
}

export function getEntryLabel(entry: ArchiveEntry | ArchivePathNode): TMessage {
  switch (entry.type) {
    case ArchiveDocumentType.BOARD_ARCHIVE_FOLDER:
      return getRootFolderLabel(ArchiveRootFolderType.BOARD_ARCHIVE);
    case ArchiveDocumentType.BOARD_MEETINGS_FOLDER:
      return { key: "meetings.board-meetings" };
    case ArchiveDocumentType.GENERAL_MEETINGS_FOLDER:
      return getRootFolderLabel(ArchiveRootFolderType.GENERAL_MEETINGS);
    case ArchiveDocumentType.SHAREHOLDER_ARCHIVE_FOLDER:
      return getRootFolderLabel(ArchiveRootFolderType.SHAREHOLDER_ARCHIVE);
    case ArchiveDocumentType.ADMIN_ARCHIVE_FOLDER:
      return getRootFolderLabel(ArchiveRootFolderType.ADMIN_ARCHIVE);
    case ArchiveDocumentType.FOLDER:
      if (entry.meetingType === MeetingType.BOARD_MEETING) {
        return {
          key: "archive.board-meeting-folder-label",
          params: {
            meetingDate: { type: "date", value: entry.meetingDate },
          },
        };
      }
      return { translated: entry.name };
    case ArchiveDocumentType.LINK:
      if ("linkTargetType" in entry) {
        switch (entry.linkTargetType) {
          case ArchiveDocumentType.BOARD_MEETING:
            return { key: "archive.board_meeting" };
          case ArchiveDocumentType.NOTICE_BOARD_MEETING:
            return { key: "archive.notice_board_meeting" };
        }
      }
      return { translated: entry.name };
    default:
      return { translated: entry.name };
  }
}

export function getEntryIcon(entry: ArchiveEntry): IconProps {
  if (entry.isFolder) {
    return { name: "folder" };
  }
  if (entry.type === ArchiveDocumentType.LINK) {
    switch (entry.linkTargetType) {
      case ArchiveDocumentType.ATTACHMENT:
        return { name: "paperclip", color: "subtle" };
      case ArchiveDocumentType.BOARD_MEETING:
      case ArchiveDocumentType.NOTICE_BOARD_MEETING:
        return { name: "file-text", color: "info" };
    }
  }
  if (isPdf(entry)) {
    return { name: "file-pdf" };
  }
  return { name: "file" };
}

function restoreResponseToNavigationTarget(response: RestoreResponse): {
  rootFolder: ArchiveRootFolderType;
  folderId?: string;
} {
  const rootFolderNode = response.path[0];
  const parentFolderId =
    response.path.length > 1 ? response.path[response.path.length - 2].id : undefined;
  return { rootFolder: documentTypeToRootFolder(rootFolderNode.type), folderId: parentFolderId };
}

function documentTypeToRootFolder(type: ArchiveDocumentType): ArchiveRootFolderType {
  switch (type) {
    case ArchiveDocumentType.BOARD_ARCHIVE_FOLDER:
      return ArchiveRootFolderType.BOARD_ARCHIVE;
    case ArchiveDocumentType.SHAREHOLDER_ARCHIVE_FOLDER:
      return ArchiveRootFolderType.SHAREHOLDER_ARCHIVE;
    case ArchiveDocumentType.GENERAL_MEETINGS_FOLDER:
      return ArchiveRootFolderType.GENERAL_MEETINGS;
    case ArchiveDocumentType.CUSTOM_ARCHIVE_FOLDER:
      return ArchiveRootFolderType.CUSTOM_ARCHIVE;
    default:
      return ArchiveRootFolderType.ADMIN_ARCHIVE;
  }
}

export function canUpload({
  rootFolder,
  data,
  userRole,
}: {
  rootFolder: ArchiveRootFolderType;
  userRole: CustomRoleForUser;
  data?: ListArchiveResponse;
}): boolean {
  if (!data) {
    return false;
  }
  if (rootFolder === ArchiveRootFolderType.BOARD_ARCHIVE) {
    return userRole.allowBoardArchiveEdit ?? false;
  }
  if (rootFolder === ArchiveRootFolderType.SHAREHOLDER_ARCHIVE) {
    return userRole.allowShareholderArchiveEdit ?? false;
  }
  if (rootFolder === ArchiveRootFolderType.ADMIN_ARCHIVE) {
    return userRole.allowAdminArchiveEdit ?? false;
  }
  if (rootFolder === ArchiveRootFolderType.CUSTOM_ARCHIVE) {
    return userRole.allowCustomArchiveEdit ?? false;
  }
  return false;
}

export const getSigningStatus = (
  entry: ArchiveEntry,
): { label: TMessage; color: "success" | "warning" } | null => {
  switch (entry.signingStatus) {
    case DocumentSigningStatus.SIGNING_COMPLETE:
      return { label: { key: "archive.signed" }, color: "success" };
    case DocumentSigningStatus.SENT_FOR_SIGNING:
    case DocumentSigningStatus.SIGNING_IN_PROGRESS:
      return { label: { key: "archive.sent-for-signing" }, color: "warning" };
    default:
      return null;
  }
};

const archiveFileTypeFilters = ["all", "folder", "pdf", "doc", "xls"] as const;

export type ArchiveFileTypeFilter = (typeof archiveFileTypeFilters)[number];

export function fileTypeFilterIcon(f: ArchiveFileTypeFilter): IconName {
  switch (f) {
    case "all":
      return "list-bullets";
    case "folder":
      return "folder";
    case "pdf":
      return "file-pdf";
    case "doc":
      return "file-doc";
    case "xls":
      return "file-xls";
  }
}

function fileTypeFilterLabel(f: ArchiveFileTypeFilter): TMessage {
  switch (f) {
    case "all":
      return { key: "archive.filter.all-filetypes" };
    case "folder":
      return { key: "archive.filter.folder" };
    case "pdf":
      return { key: "archive.filter.pdf" };
    case "doc":
      return { key: "archive.filter.doc" };
    case "xls":
      return { key: "archive.filter.xls" };
  }
}

const archiveSignaturesFilters = ["all", "sent", "signed"] as const;

export type ArchiveSignaturesFilter = (typeof archiveSignaturesFilters)[number];

export function signaturesFilterIcon(f: ArchiveSignaturesFilter): IconName {
  switch (f) {
    case "all":
      return "list-bullets";
    case "sent":
      return "paper-plane";
    case "signed":
      return "check";
  }
}

function signaturesFilterLabel(f: ArchiveSignaturesFilter): TMessage {
  switch (f) {
    case "all":
      return { key: "archive.filter.all-signatures" };
    case "sent":
      return { key: "archive.filter.sent-for-signing" };
    case "signed":
      return { key: "archive.filter.signed" };
  }
}

export function filterEntries(
  entries: ArchiveEntry[],
  inputValue: string,
  fileTypeFilter: ArchiveFileTypeFilter,
  signaturesFilter: ArchiveSignaturesFilter,
  t: TFunction,
) {
  const data = filterByFileType(entries, fileTypeFilter);
  const filteredData = filterBySignatures(data, signaturesFilter);
  if (inputValue) {
    return filteredData.filter((entry) =>
      t(getEntryLabel(entry)).toLowerCase().includes(inputValue.toLowerCase()),
    );
  } else {
    return filteredData;
  }
}

export function filterByFileType(entries: ArchiveEntry[], fileTypeFilter: ArchiveFileTypeFilter) {
  switch (fileTypeFilter) {
    case "all":
      return entries;
    case "folder":
      return entries.filter((e) => e.isFolder);
    case "doc":
      return entries.filter((e) => e.name.endsWith(".doc") || e.name.endsWith(".docx"));
    case "pdf":
      return entries.filter((e) => e.name.endsWith(".pdf"));
    case "xls":
      return entries.filter((e) => e.name.endsWith(".xls") || e.name.endsWith(".xlsx"));
  }
}

function filterBySignatures(data: ArchiveEntry[], signaturesFilter: ArchiveSignaturesFilter) {
  switch (signaturesFilter) {
    case "all":
      return data;
    case "sent":
      return data.filter(
        (e) =>
          e.signingStatus === DocumentSigningStatus.SENT_FOR_SIGNING ||
          e.signingStatus === DocumentSigningStatus.SIGNING_IN_PROGRESS,
      );
    case "signed":
      return data.filter((e) => e.signingStatus === DocumentSigningStatus.SIGNING_COMPLETE);
  }
}
